/**
 * Formatta un numero in formato italiano.
 * Separatore migliaia: punto (.)
 * Separatore decimali: virgola (,)
 * Sempre 2 cifre decimali — anche se il numero è intero.
 *
 * Esempi:
 *   formatNumber(10523.25) → "10.523,25"
 *   formatNumber(10523)    → "10.523,00"
 *   formatNumber(6487.07)  → "6.487,07"
 *   formatNumber(0.5)      → "0,50"
 *   formatNumber(null)     → "—"
 */
export function formatNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(Number(value))) return '—';
  return Number(value).toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  });
}

/**
 * Alias semantico per importi in euro.
 * Comportamento identico a formatNumber — sempre 2 decimali.
 *
 * Esempio: formatCurrency(1523.5) → "1.523,50"
 */
export function formatCurrency(value: number | null | undefined): string {
  return formatNumber(value);
}

/**
 * Alias semantico per quantità e misure.
 * Comportamento identico a formatNumber — sempre 2 decimali.
 *
 * Esempio: formatQuantity(5) → "5,00"
 */
export function formatQuantity(value: number | null | undefined): string {
  return formatNumber(value);
}

/**
 * Converte una stringa in formato italiano (o un numero) in number.
 * Gestisce separatore migliaia "." e decimale ",".
 *
 * Esempi:
 *   parseItalianNumber("6.410,58") → 6410.58
 *   parseItalianNumber("498,21")   → 498.21
 *   parseItalianNumber("6410.58")  → 6410.58   (dot come decimale, no virgola)
 *   parseItalianNumber("")         → NaN
 */
export function parseItalianNumber(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return NaN;
  if (typeof value === 'number') return value;
  let t = value.trim();
  if (t === '') return NaN;
  // Se c'è la virgola, è il separatore decimale → i punti sono migliaia.
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
  return Number(t);
}

/**
 * Formatta un valore per la visualizzazione in un campo input di importo
 * (formato italiano, 2 decimali). Ritorna stringa vuota per valori vuoti/NaN
 * così da non mostrare "—" dentro un input. Con grouping=false omette i
 * separatori delle migliaia (utile mentre il campo è in editing).
 *
 * Esempi:
 *   formatNumberInput(6410.58)               → "6.410,58"
 *   formatNumberInput("498,21000000000")     → "498,21"
 *   formatNumberInput(6410.58, false)        → "6410,58"
 *   formatNumberInput("")                     → ""
 */
export function formatNumberInput(value: string | number | null | undefined, grouping = true): string {
  const n = parseItalianNumber(value);
  if (isNaN(n)) return '';
  return n.toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: grouping,
  });
}
